import { trackUxEvent } from './analytics';

type RetentionState = {
  firstTrackedAt: number;
  ticker: string;
  lastReturnDate?: string;
  sent?: Record<string, boolean>;
};

const STORAGE_PREFIX = 'policy-intel:tracked-activation-retention:v1';
const HOUR = 60 * 60 * 1000;

function identityScope(identity: string): string {
  const normalized = String(identity ?? '').trim().toLowerCase();
  if (!normalized) return '';
  let hash = 2166136261;
  for (let i = 0; i < normalized.length; i += 1) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function storageKey(identity: string): string {
  const scope = identityScope(identity);
  return scope ? `${STORAGE_PREFIX}:${scope}` : '';
}

function readState(identity: string): RetentionState | null {
  const key = storageKey(identity);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RetentionState;
    if (!Number.isFinite(parsed?.firstTrackedAt) || !parsed?.ticker) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeState(identity: string, state: RetentionState): void {
  const key = storageKey(identity);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Analytics persistence must never block the product flow.
  }
}

export function recordTrackedActivation(ticker: string, identity: string): void {
  const normalized = String(ticker ?? '').replace(/\D/g, '');
  if (!/^\d{6}$/.test(normalized) || !identityScope(identity)) return;
  const current = readState(identity);
  if (current) return;
  writeState(identity, { firstTrackedAt: Date.now(), ticker: normalized, sent: {} });
}

export function emitTrackedReturnMilestones(source: string, identity: string): void {
  const state = readState(identity);
  if (!state) return;
  const now = Date.now();
  const elapsed = now - state.firstTrackedAt;
  if (elapsed < HOUR) return;

  const local = new Date(now);
  const today = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`;
  if (state.lastReturnDate !== today) {
    trackUxEvent('return_visit', {
      source,
      activation_ticker: state.ticker,
      hours_since_track: Number((elapsed / HOUR).toFixed(1)),
      retention_authority: 'client_identity_scoped',
    });
    state.lastReturnDate = today;
  }

  const windows = [
    { event: 'return_d1', min: 20 * HOUR, max: 48 * HOUR },
    { event: 'return_d3', min: 60 * HOUR, max: 96 * HOUR },
    { event: 'return_d7', min: 144 * HOUR, max: 216 * HOUR },
  ];
  state.sent = state.sent ?? {};
  windows.forEach(({ event, min, max }) => {
    if (state.sent?.[event] || elapsed < min || elapsed > max) return;
    trackUxEvent(event, {
      source,
      activation_ticker: state.ticker,
      hours_since_track: Number((elapsed / HOUR).toFixed(1)),
      retention_authority: 'client_identity_scoped',
    });
    state.sent![event] = true;
  });
  writeState(identity, state);
}
